import { MouseEvent, useMemo, useRef, useState } from 'react'

import { Lookup, useLookup } from '@/providers'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CloseIcon from '@mui/icons-material/Close'
import {
	Box,
	Button,
	ButtonGroup,
	ClickAwayListener,
	Divider,
	IconButton,
	InputAdornment,
	Paper,
	Popper,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import { UrlUpdateType } from 'use-query-params'

type Props = {
	brandTypePurchase: number | null
	brandTypeMatch: number | null
	updateFilters: (changes: any, updateTypeOverride?: UrlUpdateType) => void
}

export default function BrandType({ brandTypePurchase, brandTypeMatch, updateFilters }: Props) {
	const options = useLookup(Lookup.BrandOwner)
	const anchorRef = useRef<HTMLDivElement>(null)
	const [open, setOpen] = useState(false)

	const displayValue = useMemo(() => {
		const purchaseLabel = options.find((o) => o.id === brandTypePurchase)?.label
		const matchLabel = options.find((o) => o.id === brandTypeMatch)?.label
		return [purchaseLabel ? `P: ${purchaseLabel}` : null, matchLabel ? `M: ${matchLabel}` : null]
			.filter(Boolean)
			.join(', ')
	}, [brandTypePurchase, brandTypeMatch, options])

	const togglePurchase = (id: number) =>
		updateFilters({ brandTypePurchase: brandTypePurchase === id ? null : id })

	const toggleMatch = (id: number) =>
		updateFilters({ brandTypeMatch: brandTypeMatch === id ? null : id })

	const hasValue = brandTypePurchase !== null || brandTypeMatch !== null

	const handleClear = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation()
		updateFilters({ brandTypePurchase: null, brandTypeMatch: null })
	}

	const handleClickAway = (event: globalThis.MouseEvent | TouchEvent) => {
		if (anchorRef.current?.contains(event.target as Node)) return
		setOpen(false)
	}

	return (
		<>
			<TextField
				ref={anchorRef}
				fullWidth
				size="small"
				label="Brand Type"
				value={displayValue}
				onClick={() => setOpen((v) => !v)}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
						e.preventDefault()
						setOpen(true)
					}
					if (e.key === 'Escape') {
						e.preventDefault()
						setOpen(false)
					}
				}}
				slotProps={{
					input: {
						readOnly: true,
						sx: { cursor: 'pointer' },
						endAdornment: (
							<InputAdornment position="end">
								{hasValue && (
									<IconButton
										size="small"
										onClick={handleClear}
										aria-label="Clear brand type filter"
										data-testid="brand-type-filter-clear"
										sx={{ p: 0.25 }}
									>
										<CloseIcon fontSize="small" />
									</IconButton>
								)}
								<ArrowDropDownIcon
									sx={{
										transition: 'transform 0.2s',
										transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
									}}
								/>
							</InputAdornment>
						),
					},
					inputLabel: { shrink: Boolean(displayValue) || open || undefined },
				}}
				data-testid="brand-type-filter-trigger"
			/>
			<Popper
				open={open}
				anchorEl={anchorRef.current}
				placement="bottom-start"
				sx={{ zIndex: (theme) => theme.zIndex.modal }}
			>
				<ClickAwayListener onClickAway={handleClickAway}>
					<Paper
						elevation={8}
						sx={{ width: '15rem', mt: 0.5 }}
						data-testid="brand-type-filter-menu"
					>
						<Stack p={2} spacing={2}>
							<Box>
								<Typography variant="body1" gutterBottom>
									Purchase
								</Typography>
								<ButtonGroup fullWidth color="secondary" orientation="vertical" variant="outlined">
									{options.map((o) => {
										const selected = brandTypePurchase === o.id
										return (
											<Button
												key={o.id}
												variant={selected ? 'contained' : 'outlined'}
												onClick={() => togglePurchase(o.id as number)}
												data-testid={`brand-type-purchase-${o.id}`}
											>
												{o.label}
											</Button>
										)
									})}
								</ButtonGroup>
							</Box>
							<Divider />
							<Box>
								<Typography variant="body1" gutterBottom>
									Match
								</Typography>
								<ButtonGroup fullWidth color="secondary" orientation="vertical" variant="outlined">
									{options.map((o) => {
										const selected = brandTypeMatch === o.id
										return (
											<Button
												key={o.id}
												variant={selected ? 'contained' : 'outlined'}
												onClick={() => toggleMatch(o.id as number)}
												data-testid={`brand-type-match-${o.id}`}
											>
												{o.label}
											</Button>
										)
									})}
								</ButtonGroup>
							</Box>
						</Stack>
					</Paper>
				</ClickAwayListener>
			</Popper>
		</>
	)
}
